import React, { useContext, useEffect } from 'react';
import { PulseLoader } from 'react-spinners';
import { Box, Button, Stack, SxProps, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { SearchBlock, ChatList, Dialog, NoChatsPage } from '../index';
import { border } from 'src/const';
import { useConnection } from 'src/hooks/useConnection';
import { useWindowSize } from 'src/hooks/useWindowSize';
import { chatsActions, ChatsContext } from 'src/context/chats';
import { ReactComponent as Back } from 'src/icons/back.svg';
import { profileActions } from 'src/context/profile';
import { buildDataTestIdAttribute, buildFullDataTestId } from 'src/utils/functionHelpers';

export const DATA_TEST_ID_MESSAGES = 'messages';

const classes = {
  box_wrapper: {
    backgroundColor: 'background.paper',
    border: border,
    height: '100%',
  } as SxProps,
  back_button: {
    color: 'grey.700',
    fontSize: '16px',
    lineHeight: '24px',
    height: '24px',
    width: '70px',
    marginBottom: '18px',
  } as SxProps,
};

const handleBackClick = () => {
  chatsActions.setActiveChat('');
  chatsActions?.setActiveChatLabCode('');
};

export const Main: React.FC = () => {
  const size = useWindowSize();
  const { activeChat, chats, firstLoadCompleted, firstLoading } = useContext(ChatsContext);

  useConnection();

  useEffect(() => {
    profileActions?.getProfilePermissions();
  }, []);

  const showChats =
    (!firstLoading && !firstLoadCompleted) || // We don't know if there are chats yet
    (firstLoading && !firstLoadCompleted) || // First download in progress
    (firstLoadCompleted && chats.length > 0); // The first download is complete and the chats are there

  const showNoChatsPage = firstLoadCompleted && chats.length === 0 && !activeChat;

  return (
    <>
      {!size.isMobileSize ? (
        <>
          <Typography color="grey.700" variant="head32Bold">
            Messages
          </Typography>
          {showChats ? (
            <Grid2
              container
              spacing={0}
              sx={{
                flexWrap: 'nowrap',
                marginTop: '36px',
                height: 'calc(100vh - 208px)',
              }}
            >
              <Grid2
                sx={{
                  height: '100%',
                  minWidth: `${size.isTabletSize ? '242px' : '372px'}`,
                  maxWidth: `${size.isTabletSize ? '242px' : ''}`,
                }}
              >
                <Stack
                  sx={{
                    ...classes.box_wrapper,
                  }}
                >
                  <SearchBlock
                    isTablet={size.isTabletSize}
                    dataTestId={buildFullDataTestId(DATA_TEST_ID_MESSAGES, 'main', 'input-search')}
                  />
                  <ChatList dataTestId={buildFullDataTestId(DATA_TEST_ID_MESSAGES, 'main', 'chat-list')} />
                </Stack>
              </Grid2>
              <Grid2
                sx={{
                  height: '100%',
                  minWidth: '456px',
                  maxWidth: '1411px',
                  width: '1411px',
                }}
              >
                <Box
                  sx={{
                    ...classes.box_wrapper,
                    borderLeft: 'none',
                  }}
                >
                  <Dialog dataTestId={buildFullDataTestId(DATA_TEST_ID_MESSAGES, 'main', 'dialog')} />
                </Box>
              </Grid2>
            </Grid2>
          ) : showNoChatsPage ? (
            <NoChatsPage size={size.isTabletSize ? 'tablet' : 'desktop'} />
          ) : null}
        </>
      ) : (
        <>
          {!activeChat && (
            <Typography color="grey.700" variant="head32Bold" marginBottom="36px">
              Messages
            </Typography>
          )}
          {activeChat && (
            <Button
              startIcon={<Back />}
              variant="text"
              onClick={handleBackClick}
              sx={classes.back_button}
              {...buildDataTestIdAttribute(DATA_TEST_ID_MESSAGES, 'main-button-back')}
            >
              Back
            </Button>
          )}
          {showChats || activeChat ? (
            <Grid2
              container
              spacing={0}
              sx={{
                flexWrap: 'nowrap',
                height: `calc(${window.innerHeight}px - ${activeChat ? '180px' : '198px'})`,
                width: '100%',
              }}
            >
              <Grid2
                sx={{
                  height: '100%',
                  width: '100%',
                }}
              >
                <Stack
                  sx={{
                    ...classes.box_wrapper,
                  }}
                >
                  {activeChat ? (
                    <Dialog dataTestId={buildFullDataTestId(DATA_TEST_ID_MESSAGES, 'main', 'dialog-mobile')} />
                  ) : (
                    <>
                      <SearchBlock
                        isMobile
                        dataTestId={buildFullDataTestId(DATA_TEST_ID_MESSAGES, 'main', 'input-search-mobile')}
                      />
                      <ChatList dataTestId={buildFullDataTestId(DATA_TEST_ID_MESSAGES, 'main', 'chat-list-mobile')} />
                    </>
                  )}
                </Stack>
              </Grid2>
            </Grid2>
          ) : showNoChatsPage ? (
            <NoChatsPage size="mobile" />
          ) : null}
        </>
      )}
      {!firstLoadCompleted && (
        <Stack
          position="absolute"
          width="100%"
          height="100%"
          bgcolor="#EFF8FA"
          zIndex={2000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            '@keyframes react-spinners-PulseLoader-pulse': {
              '0%': {
                transform: 'scale(1)',
                opacity: 1,
              },
              '45%': {
                transform: 'scale(0.1)',
                opacity: 0.7,
              },
              '80%': {
                transform: 'scale(1)',
                opacity: 1,
              },
            },
          }}
        >
          <PulseLoader color="#0096b2" size={20} />
        </Stack>
      )}
    </>
  );
};
